import { computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Event, NavigationEnd, Router } from "@angular/router";
import { setError, setLoaded, setLoading, withCallState } from "@dcc/shared/state";
import { getLocalStorageItemWithTTL, setLocalStorageItemWithTTL } from "@dcc/shared/util-localstorage";
import { UserStore } from "@dcc/vestas-online/data-access";
import { tapResponse } from "@ngrx/operators";
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { distinctUntilChanged, filter, map, switchMap, tap, timer } from "rxjs";
import { getLocalizedMessage, getPlatformAlert, mapSeverityToAppearance } from "./message-utils";
import { PlatformAlert, PlatformApplicationAlert } from "./system-message.model";
import { SystemMessagesService } from "./system-messages.service";

const POLLING_INTERVAL_MS: number = 10 * 60 * 1000; // 10 minutes;
const DISMISSED_TIMEOUT = 1000 * 60 * 60 * 12; // 12 hours;
const DISMISS_LOCAL_STORAGE_ID = "vo-platform-alert-dismissed";

export type PlatformAlertsState = {
    platformApplicationAlerts: PlatformApplicationAlert[];
    platformAlert: PlatformAlert | null;
    dismissedAlertId: number | null;
};

export const PlatformAlertsStore = signalStore(
    { providedIn: "root" },
    withProps(() => ({
        _router: inject(Router),
        _route: inject(ActivatedRoute),
        _userStore: inject(UserStore)
    })),
    withState<PlatformAlertsState>({
        dismissedAlertId: null,
        platformApplicationAlerts: [],
        platformAlert: null
    }),
    withCallState(),
    withComputed(({ dismissedAlertId, platformAlert, platformApplicationAlerts, _router, _route, _userStore }) => {
        const applicationId = toSignal(
            _router.events.pipe(
                filter((routerEvent: Event) => routerEvent instanceof NavigationEnd),
                map(() => {
                    let applicationId = null;
                    let child = _route?.firstChild;

                    if (child) {
                        while (child.firstChild) {
                            child = child.firstChild;
                        }

                        applicationId = child.snapshot?.data["applicationId"] ?? null;
                    }
                    return applicationId;
                }),
                distinctUntilChanged()
            )
        );

        return {
            alert: computed(() => {
                const alert = platformAlert() ?? platformApplicationAlerts().find((message) => message.applicationId === applicationId());

                if (!alert || alert?.id === dismissedAlertId()) {
                    return null;
                }

                return {
                    id: alert.id,
                    dismissable: alert.dismissable ?? false,
                    message: getLocalizedMessage(_userStore.language(), alert),
                    appearance: mapSeverityToAppearance(alert)
                };
            })
        };
    }),
    withMethods((store, platformMessagesService = inject(SystemMessagesService)) => {
        return {
            dismissMessage: (id: number) => {
                setLocalStorageItemWithTTL(DISMISS_LOCAL_STORAGE_ID, id, DISMISSED_TIMEOUT);
                patchState(store, { dismissedAlertId: id });
            },
            startPollingSystemMessages: rxMethod<void>(() =>
                timer(0, POLLING_INTERVAL_MS).pipe(
                    tap(() => patchState(store, setLoading())),
                    switchMap(() =>
                        platformMessagesService.getAll().pipe(
                            tapResponse({
                                next: (response) =>
                                    patchState(
                                        store,
                                        {
                                            platformApplicationAlerts: response,
                                            platformAlert: getPlatformAlert(response)
                                        },
                                        setLoaded()
                                    ),
                                error: (errorMsg: string) => patchState(store, setError(errorMsg))
                            })
                        )
                    )
                )
            )
        };
    }),
    withHooks({
        onInit: ({ startPollingSystemMessages, ...store }) => {
            startPollingSystemMessages();

            const dismissedAlertId = getLocalStorageItemWithTTL(DISMISS_LOCAL_STORAGE_ID) as number | null;
            if (dismissedAlertId !== null) {
                patchState(store, { dismissedAlertId });
            }
        }
    })
);
